/**
 * HeroSpikeCard — the #1 spike on the Trending Today page (bd deepcontext-yx28,
 * superseding the full-bleed gradient card from bd deepcontext-d68.22).
 *
 * Page-lock 3 mockup: a plain `--surface` card — uppercase accent kicker,
 * ink title, URL/delta/impressions metrics (delta in safe/risk ink) and a
 * filled sparkline in the theme's series-1 purple (bd deepcontext-fgvn's
 * categorical palette, slot 0 — always this cluster's colour since the hero
 * is always rank #1).
 *
 * Click semantics unchanged: pressing the card emits `onSelect(cluster.id)`
 * so the parent can toggle the `?cluster=<id>` drill.
 */

import React from 'react';
import {useChartPalette} from '../../../components/charts/useChartMode';
import type {TrendingClusterPayload} from '../../../api/clusters';
import {formatDelta} from '../utils/formatDelta';

export interface HeroSpikeCardProps {
    cluster: TrendingClusterPayload;
    selected?: boolean;
    onSelect?: (clusterId: string) => void;
    /**
     * False while `payload.snapshot_hours < 24` (bd deepcontext-ws2b.3) — the
     * hourly snapshot history isn't complete yet, so vs-prior-24h growth is
     * unreliable and is hidden rather than shown misleadingly. Defaults to
     * true (fully warmed up).
     */
    showGrowth?: boolean;
}

const SVG_WIDTH = 300;
const SVG_HEIGHT = 60;

function buildSparklinePaths(points: number[]): {line: string; area: string} {
    const safe = points.length > 0 ? points : [0, 0];
    const max = Math.max(...safe);
    const min = Math.min(...safe);
    const denom = Math.max(1, max - min);
    const sx = SVG_WIDTH / Math.max(1, safe.length - 1);
    const sy = (SVG_HEIGHT - 4) / denom;
    const line = safe
        .map(
            (p, i) =>
                `${i === 0 ? 'M' : 'L'} ${(i * sx).toFixed(2)} ${(
                    SVG_HEIGHT -
                    2 -
                    (p - min) * sy
                ).toFixed(2)}`,
        )
        .join(' ');
    const area = `${line} L ${SVG_WIDTH} ${SVG_HEIGHT} L 0 ${SVG_HEIGHT} Z`;
    return {line, area};
}

const HeroSpikeCard: React.FC<HeroSpikeCardProps> = ({
    cluster,
    selected,
    onSelect,
    showGrowth = true,
}) => {
    const palette = useChartPalette();
    // Always slot 0 (series-1 purple) — the hero is always the #1 spike.
    const seriesColor = palette.categorical[0];
    const {line, area} = buildSparklinePaths(cluster.sparkline);
    const delta = formatDelta(cluster.delta_pct);
    const headline = cluster.topics[0]?.phrase ?? cluster.label;
    const subtopics = cluster.topics.slice(1, 5);
    const gradientId = `hero-spark-${cluster.id}`;
    const ring = selected ? 'ring-2 ring-inset ring-accent' : 'hover:shadow-lg';

    return (
        <button
            type="button"
            onClick={() => onSelect?.(cluster.id)}
            data-testid={`trending-hero-card-${cluster.id}`}
            aria-pressed={selected ?? false}
            className={`relative w-full text-left rounded-2xl overflow-hidden p-5 bg-surface border border-line shadow-sm flex flex-col justify-between min-h-[220px] transition-all ${ring}`}
        >
            <div>
                <div className="text-xs uppercase tracking-[0.18em] font-bold text-accent-active mb-1 flex items-center gap-2">
                    <span>Top today · #1</span>
                </div>
                <div
                    data-testid={`trending-hero-headline-${cluster.id}`}
                    className="font-extrabold tracking-tight text-2xl sm:text-3xl leading-[1.1] text-ink max-w-xl"
                >
                    {headline}
                </div>
                {subtopics.length > 0 && (
                    <div
                        data-testid={`trending-hero-subtopics-${cluster.id}`}
                        className="text-sm mt-2 text-ink-muted max-w-md leading-snug"
                    >
                        {subtopics.map((t) => t.phrase).join(' · ')}
                    </div>
                )}
            </div>

            <div className="flex items-end justify-between gap-6 mt-4">
                <div className="flex gap-5 text-sm text-ink-muted font-semibold">
                    <div>
                        <div className="font-extrabold leading-none text-lg tabular-nums text-ink">
                            {cluster.current_count.toLocaleString()}
                        </div>
                        <div className="mt-1">Ad requests · 24h</div>
                    </div>
                    {showGrowth && (
                        <div>
                            <div className={`font-extrabold leading-none text-lg tabular-nums ${delta.toneClass}`}>
                                {delta.text}
                            </div>
                            <div className="mt-1">vs previous 6h</div>
                        </div>
                    )}
                    
                </div>
                <svg
                    viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
                    className="h-11 w-full max-w-[220px] shrink-0"
                    preserveAspectRatio="none"
                    data-testid={`trending-hero-sparkline-${cluster.id}`}
                    role="img"
                    aria-label={`${cluster.label} 24h sparkline`}
                >
                    <defs>
                        <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
                            <stop offset="0" stopColor={seriesColor} stopOpacity="0.16" />
                            <stop offset="1" stopColor={seriesColor} stopOpacity="0" />
                        </linearGradient>
                    </defs>
                    <path d={area} fill={`url(#${gradientId})`} />
                    <path
                        d={line}
                        stroke={seriesColor}
                        strokeWidth={2}
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </div>
        </button>
    );
};

export default HeroSpikeCard;
